import getConfig from "next/config";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import type { Recording } from "src/types";

import { ContentLibraryDialog } from "@/components/shared/content-library/content-library-dialog";
import { ContentLibrary } from "@/components/shared/content-library/content-library-loader";
import { useContentLibraryTranslations } from "@/components/shared/content-library/use-content-library-translations";
import { trackLibraryVideoSourceUpdate } from "@/content-hub/analytics/video-library-events";
import { useContentPageRouteValues } from "@/content-hub/editor/use-content-page-route-values";
import { useSpaceFilter } from "@/content-hub/editor/use-space-filter";
import { logProductAnalyticsEvent } from "@/features/analytics";

const { publicRuntimeConfig } = getConfig();

export type RecordingLibraryPickerProps = {
  recordingId?: string | null;
  recording?: Recording;
  onRecordingSelected: (recordingId: string) => void;
  className?: string;
  sectionLabel?: string;
  /** When true, ignore confirm if the user re-selected the current recording. */
  skipSameRecording?: boolean;
  /** Hide preview when Details already shows the thumbnail (direct video sharing). */
  showPreviewThumbnail?: boolean;
};

/** Shared Content Library single-select (single Video library tab + grid video source). */
export function RecordingLibraryPicker({
  recordingId,
  recording,
  onRecordingSelected,
  className = "",
  sectionLabel,
  skipSameRecording = false,
  showPreviewThumbnail = true,
}: RecordingLibraryPickerProps) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { organizationExternalId } = useContentPageRouteValues();
  const spaceExternalId = useSpaceFilter();
  const translations = useContentLibraryTranslations();
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);

  return (
    <div className={`flex flex-col gap-y-4 ${className}`.trim()}>
      {sectionLabel ? (
        <div className="text-xs font-medium text-neutral-b1">
          {sectionLabel}
        </div>
      ) : null}
      {showPreviewThumbnail && recording?.thumbnail ? (
        <div className="overflow-hidden rounded-[4px]">
          <img
            className="aspect-[16/9] w-full object-cover"
            alt={recording.title || ""}
            src={recording.thumbnail}
          />
        </div>
      ) : null}
      <button
        type="button"
        className="h-10 rounded-md bg-primary px-4 py-2 text-sm text-white"
        onClick={() => setIsLibraryOpen(true)}
      >
        {t("preference-panel.choose-video")}
      </button>
      <ContentLibraryDialog
        isOpen={isLibraryOpen}
        onClose={() => setIsLibraryOpen(false)}
        unmount={true}
      >
        <ContentLibrary
          options={{
            host: publicRuntimeConfig.HOPIN_ROOT,
            organisationId: organizationExternalId,
            spaceExternalId,
            i18n: {
              locale: i18n.language,
              translations,
            },
          }}
          onClose={() => setIsLibraryOpen(false)}
          selectedRecordingIds={recordingId ? [recordingId] : undefined}
          allowSingleSelection={true}
          onConfirmSelection={(recordingIds) => {
            const nextRecordingId = recordingIds[0];
            if (!nextRecordingId) return;
            if (skipSameRecording && nextRecordingId === recordingId) {
              return;
            }

            dispatch({
              type: "[page editor] fetch recordings",
              payload: {
                organizationExternalId,
                recordingIds,
              },
            });
            onRecordingSelected(nextRecordingId);
            trackLibraryVideoSourceUpdate({
              kind: "recording",
              recordingId: nextRecordingId,
            });
            logProductAnalyticsEvent({
              name: "ch_canvas_block_edit_video_lib",
              properties: { recordingId: nextRecordingId },
            });
          }}
        />
      </ContentLibraryDialog>
    </div>
  );
}
